import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2,
  CreditCard,
  Eye,
  EyeOff,
  Save,
  Unlink2,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

export default function StripeSettings() {
  const [status, setStatus] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/stripe/status");
      setStatus(r.data);
    } catch (e) {
      toast.error("Failed to load Stripe status", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function save() {
    const k = keyInput.trim();
    if (!k) {
      toast.error("Paste your Stripe restricted key first");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/stripe/credentials", { restricted_key: k });
      setStatus(r.data);
      setKeyInput("");
      toast.success("Stripe connected", {
        description: `${r.data.mode === "live" ? "Live" : "Test"} mode${r.data.last_verified_account ? ` · ${r.data.last_verified_account}` : ""}`,
      });
    } catch (e) {
      toast.error("Stripe key rejected", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect Stripe? You'll need to paste the key again to reconnect.")) return;
    setDisconnecting(true);
    try {
      await api.delete("/stripe/credentials");
      await refresh();
      toast.success("Stripe disconnected");
    } catch (e) {
      toast.error("Disconnect failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDisconnecting(false);
    }
  }

  const configured = !!status?.configured;

  return (
    <Card className="p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none" data-testid="stripe-settings-card">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
        <CreditCard className="h-3.5 w-3.5" /> Stripe — Payment reconciliation
      </div>

      <h2 className="mt-2 font-display text-xl font-bold tracking-tight text-zinc-950">
        Stripe restricted key
      </h2>
      <p className="mt-1 text-sm text-zinc-600">
        Read-only Stripe access so we can show the FULL subscription/membership renewal payment
        history that Acuity's public API hides (Acuity only exposes the first payment of a subscription).
      </p>

      {configured ? (
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">Status</div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-emerald-700 font-medium">
              <CheckCircle2 className="h-4 w-4" /> Connected ({status.mode || "?"} mode)
            </div>
          </div>
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">Last verified</div>
            <div className="mt-1 font-mono-num text-zinc-900">
              {status.last_verified_iso ? fmtDateTime(status.last_verified_iso) : "—"}
            </div>
            {status.last_verified_account && (
              <div className="mt-0.5 text-xs text-zinc-500 truncate">
                {status.last_verified_account}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-900">
          Not connected. Paste a restricted key below to enable Stripe reconciliation.
        </div>
      )}

      <div className="mt-5">
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">
          {configured ? "Replace key" : "Restricted key"}
        </Label>
        <div className="mt-1.5 flex gap-2">
          <div className="flex-1 relative">
            <Input
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              type={showKey ? "text" : "password"}
              placeholder={configured ? "••••••••••• (key saved — paste new key to rotate)" : "rk_live_..."}
              className="border-zinc-300 font-mono-num pr-9"
              data-testid="stripe-key-input"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
              tabIndex={-1}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <Button
            onClick={save}
            disabled={saving || !keyInput.trim()}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="stripe-save-btn"
          >
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? "Verifying…" : configured ? "Replace" : "Save & verify"}
          </Button>
          {configured && (
            <Button
              variant="outline"
              onClick={disconnect}
              disabled={disconnecting}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="stripe-disconnect-btn"
            >
              <Unlink2 className="h-4 w-4 mr-1.5" />
              Disconnect
            </Button>
          )}
        </div>
      </div>

      <details className="mt-4 text-xs text-zinc-600">
        <summary className="cursor-pointer text-zinc-700 font-medium">How to create the restricted key</summary>
        <ol className="mt-2 list-decimal pl-5 space-y-1">
          <li>Stripe Dashboard → <b>Developers</b> → <b>API keys</b> → <b>Restricted keys</b> → <b>+ Create restricted key</b></li>
          <li>Name it <span className="font-mono-num">Acuity Reports — Reconciliation</span></li>
          <li>Set <b>Read</b> for: Customers, Charges, Payment Intents, Invoices, Subscriptions, Balance Transactions</li>
          <li>Leave everything else as <b>None</b></li>
          <li>Create → copy the <span className="font-mono-num">rk_live_…</span> key → paste above</li>
        </ol>
      </details>
    </Card>
  );
}
